<template>
    <transition name="fade">
      <div v-if="visible" class="fixed" @click.stop>
        <div class="bg-white popup">
          <h2 class="title">{{ props.coupon ? 'Editar cupón' : 'Añadir cupón' }}</h2>
          <img src="@/assets/images/ic_close.svg" class="close_icon" @click="closePopup()" />
          <div class="fields">
            <div class="field-group">
              <label for="code">Código*</label>
              <input
                id="code"
                type="text"
                placeholder="Ingrese el código del cupón"
                required
                v-model="code"
                :disabled="props.movement ? true : false" 
                maxlength="6" />
            </div>

            <div class="field-group">
              <label for="type">Tipo de descuento</label>
              <select v-model="type">
                <option value="euros">Euros</option>
                <option value="porcentaje">Porcentaje</option>
              </select>
            </div>

            <div class="field-group">
              <label for="amount">Valor del descuento*</label>
              <input
                id="amount"
                type="number"
                step="0.01"
                inputmode="decimal"
                placeholder="Ingrese el valor del descuento"
                required
                v-model="discountValue" />
            </div>

            <div class="field-group">
              <label for="min_purchase">Compra mínima para poder aplicar el descuento</label>
              <input
                id="min_purchase"
                type="number"
                step="0.01"
                inputmode="decimal"
                placeholder="Ingrese la compra mínima si existe"
                required
                v-model="minPurchase" />
            </div>

            <div class="field-group">
              <label for="expiration">Fecha de caducidad del cupón (o vacío para que no caduque)</label>
              <input
                id="expiration"
                type="date"
                v-model="expirationDate" />
            </div>

            <div class="field-group">
              <label for="max_uses">Usos máximos</label>
              <input
                id="max_uses"
                type="number"
                step="1"
                placeholder="Ingrese los usos máximos si existen"
                required
                v-model="maxUses" />
            </div>

            <div class="field-group">
              <label for="unique_user">Usuario que puede usar el cupón</label>
              <input
                id="unique_user"
                type="text"
                placeholder="Indica el usuario que puede usar el cupón (vacío para todos)"
                v-model="uniqueUser" />
            </div>

            <div class="field-group">
              <label for="platform">Plataforma</label>
              <select v-model="platform">
                <option value="todas">Todas</option>
                <option value="app">App</option>
                <option value="web">Web</option>
              </select>
            </div>

            <div class="checkbox-group">
              <label class="checkbox-label">
                <input type="checkbox" name="activo" v-model="active"> 
                Activo
              </label>

              <label class="checkbox-label">
                <input type="checkbox" name="onlyFirstPurchase" v-model="onlyFirstPurchase"> 
                Sólo primera compra
              </label>
            </div>

            <div class="button-group">
              <button @click="closePopup()" class="secondary_button">Cancelar</button>
              <button @click="saveClicked()" class="main_button">Guardar</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </template>
  
  <script setup>
  import { ref, watch, defineEmits, defineProps } from 'vue';
  import { useLoadingStore } from "@/stores/loadingStore";
  import { refreshToken } from "@/services/authService";
  import { useRouter } from 'vue-router';

  const props = defineProps({
    showCouponPopup: Boolean,
    coupon: Object,
  });
  
  const loadingStore = useLoadingStore();
  const router = useRouter();

  const visible = ref(false);
  const code = ref("");
  const type = ref("euros");
  const discountValue = ref(0);
  const minPurchase = ref(0);
  const expirationDate = ref(null);
  const maxUses = ref(0);
  const uniqueUser = ref("");
  const platform = ref("todas");
  const active = ref(true);
  const onlyFirstPurchase = ref(false);

  const emit = defineEmits(['couponSaved', 'couponPopupCancelled', 'errorHappened']);

  watch(() => props.showCouponPopup, (newVal) => {
    visible.value = newVal;
  });

  watch(() => props.coupon, (newVal) => {
    code.value = newVal?.codigo || "";
    type.value = newVal?.tipo_descuento || "euros";
    discountValue.value = newVal?.valor_descuento || 0;
    minPurchase.value = newVal?.compra_minima || 0;
    if (newVal?.fecha_caducidad != null && newVal?.fecha_caducidad !== "" && newVal?.fecha_caducidad !== "Sin caducidad") {
      expirationDate.value = toInputFormat(newVal.fecha_caducidad);
    } else {
      expirationDate.value = null;
    }
    maxUses.value = newVal?.usos_maximo || 0;
    uniqueUser.value = newVal?.usuario_unico || "";
    platform.value = newVal?.platform || "todas";
    active.value = newVal?.activo || false;
    onlyFirstPurchase.value = newVal?.only_first_purchase || false;
  });

  const closePopup = () => {
    visible.value = false;
    code.value = "";
    type.value = "euros";
    discountValue.value = 0;
    minPurchase.value = 0;
    expirationDate.value = null;
    maxUses.value = 0;
    uniqueUser.value = "";
    platform.value = "todas";
    active.value = true;
    onlyFirstPurchase.value = false;
    emit('couponPopupCancelled');
  };

  function toInputFormat(dateStr) {
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  }


  const saveClicked = async () => {
    if (code.value.trim() === "" || discountValue.value <= 0 || maxUses.value < 0) {
      emit('errorHappened', 'Por favor, complete todos los campos obligatorios correctamente.');
      return;
    }
    if (code.value.trim().length < 6) {
      emit('errorHappened', 'El código del cupón debe tener 6 caracteres.');
      return;
    }

    loadingStore.setLoading(true);

    try {
      let response = await callAPIToSaveCoupon();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await callAPIToSaveCoupon();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        loadingStore.setLoading(false);
        emit('errorHappened', 'Se ha producido un error guardando el cupón.');
        return;
      }

      const data = await response.json();

      if (data.errors) {
        loadingStore.setLoading(false);
        emit('errorHappened', data.message);
      } else {
        loadingStore.setLoading(false);
        emit('couponSaved');
        closePopup();
      }
    } catch (exception) {
      emit('errorHappened', exception.message);
    }

  }

  async function callAPIToSaveCoupon(){
    if (props.coupon == null) {
      return await fetch(process.env.VUE_APP_API_URL+"api/god/coupons/add", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "vgod": process.env.VUE_APP_API_VGOD,
            "cp-token": process.env.VUE_APP_API_CP_TOKEN,
            "Authorization": "Bearer " + localStorage.getItem('token')
          },
          body: JSON.stringify({
            "user_id": localStorage.getItem('user_id'),
            "code": code.value,
            "discount_type": type.value,
            "discount_value": discountValue.value,
            "min_purchase": minPurchase.value,
            "expiration_date": expirationDate.value ?? null,
            "max_uses": maxUses.value,
            "unique_user": uniqueUser.value,
            "platform": platform.value,
            "active": active.value ? 1 : 0,
            "only_first_purchase": onlyFirstPurchase.value ? 1 : 0,
          }),
        });
    } else {
      return await fetch(process.env.VUE_APP_API_URL+"api/god/coupons/edit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "vgod": process.env.VUE_APP_API_VGOD,
            "cp-token": process.env.VUE_APP_API_CP_TOKEN,
            "Authorization": "Bearer " + localStorage.getItem('token')
          },
          body: JSON.stringify({
            "user_id": localStorage.getItem('user_id'),
            "coupon_id": props.coupon.id,
            "code": code.value,
            "discount_type": type.value,
            "discount_value": discountValue.value,
            "min_purchase": minPurchase.value,
            "expiration_date": expirationDate.value ?? null,
            "max_uses": maxUses.value,
            "unique_user": uniqueUser.value,
            "platform": platform.value,
            "active": active.value ? 1 : 0,
            "only_first_purchase": onlyFirstPurchase.value ? 1 : 0,
          }),
        });
    }
  }

  </script>
  
  <style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  
  .fixed {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
  
  .bg-white {
    background-color: white;
    padding: 30px;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
  }

  .popup {
    position: relative;
  }

  .title {
    border-bottom: 1px solid var(--very-clear-gray);
    text-align: center;
    margin: 0 0 var(--spacing-lg) 0;
    padding-bottom: var(--spacing-md);
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .field-group label {
    font-weight: 600;
    color: var(--text-color-dark);
    font-size: 14px;
  }

  .field-group input,
  .field-group select {
    width: 100%;
  }

  .field-group input:disabled {
    background-color: var(--almost-white) !important;
    border-color: var(--white) !important;
    cursor: not-allowed;
    opacity: 0.6;
  }

  .checkbox-group {
    display: flex;
    gap: var(--spacing-md);
    padding: var(--spacing-md) 0;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex: 1;
    justify-content: center;
    padding: var(--spacing-sm);
    border-radius: var(--radius-md);
    transition: background-color var(--transition-fast);
  }

  .checkbox-label:hover {
    background-color: var(--almost-white);
  }

  .checkbox-label input[type="checkbox"] {
    margin: 0;
  }

  .button-group {
    display: flex;
    gap: var(--spacing-md);
    margin-top: var(--spacing-lg);
  }

  .button-group button {
    flex: 1;
  }

  .close_icon {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 30px;
    height: 30px;
    cursor: pointer;
    transition: transform var(--transition-fast);
  }

  .close_icon:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    .bg-white {
      width: 95%;
      padding: 20px;
    }

    .checkbox-group {
      flex-direction: column;
      gap: var(--spacing-sm);
    }

    .button-group {
      flex-direction: column;
    }
  }
  </style>
  